/**
 * A consultation's video call in the session panel: the „Rozmowa wideo" switch, the link field,
 * `PUT /calendar-event/{id}/call`, and the short wait for a Google Meet (consultation-calls plan, PR 2).
 *
 * What the panel offers is `model/callLink`'s `callPanel`; this owns the field, the confirm before a
 * link is switched away, the write and what is said after it. While a Meet is on its way the schedule
 * is read again every 5 s for a minute — Google makes it within seconds, or the reconcile's sweep does
 * much later, and by then the trainer has been offered to paste one.
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { googleCalendarQueryOptions } from '~/entities/google-calendar-connection';
import { BOOKING_ROOT, readBookingAgain, type FailedWrite } from '~/features/credit-ledger';
import { usePutCalendarEventIdCall } from '~/shared/api/generated/endpoints/calendar-event/calendar-event';
import type {
  TrainWithMeModulesTrainingDtosResponsesCalendarEventResponse as CalendarEventResponse,
  TrainWithMeModulesTrainingDtosRequestsPutCalendarEventCallRequest as PutCallRequest,
} from '~/shared/api/generated/model';
import { ApiError } from '~/shared/api/mutator';
import { isApplePlatform } from '~/shared/lib/applePlatform';
import { useAnnouncer } from '~/shared/lib/useAnnouncer';
import type { ConfirmPrompt } from '~/shared/ui/ConfirmDialog';

import type { BookingSession } from '../model/bookingSession';
import { callPanel, checkCallLink, googleMeetSourceOf, type CallPanel } from '../model/callLink';
import { callLinkHelperKey } from '../model/callLinkChoice';
import { writeRefusal } from '../model/writeRefusal';

const KEY = 'web:booking.call';
const MEET_POLL_MS = 5_000;
const MEET_WAIT_MS = 60_000;

type CallWrite = 'on' | 'off' | 'saved' | 'removed' | 'meet';

export interface SessionCallState {
  readonly panel: CallPanel;
  /** The link field, open — its text and why it cannot be sent, once a send was tried. */
  readonly field: { readonly value: string; readonly problem: string | null } | null;
  readonly saving: boolean;
  /** The field just closed (saved or cancelled): focus goes back to the join link or the act that opened it. */
  readonly returnFocus: boolean;
  readonly focusReturned: () => void;
  readonly refusal: string | null;
  readonly prompt: ConfirmPrompt | null;
  readonly announcement: string;
  readonly setVideo: (on: boolean) => void;
  readonly openField: () => void;
  readonly editField: (value: string) => void;
  readonly closeField: () => void;
  readonly saveLink: () => void;
  readonly removeLink: () => void;
  /** „Użyj Google Meet": the pasted or standing link goes, and Google makes a Meet. */
  readonly useMeet: () => void;
  /** The suggestions under the link field, by platform. */
  readonly helperKey: string;
  readonly confirm: () => void;
  readonly dismiss: () => void;
}

function failureOf(error: unknown): FailedWrite | null {
  return error instanceof ApiError ? { status: error.status, code: error.code } : null;
}

export function useSessionCall(session: BookingSession): SessionCallState {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const announcer = useAnnouncer();
  const google = useQuery(googleCalendarQueryOptions());
  const [meetWaitOver, setMeetWaitOver] = useState(false);
  const [field, setField] = useState<{ value: string; tried: boolean } | null>(null);
  const [askingOff, setAskingOff] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [returnFocus, setReturnFocus] = useState(false);
  const panel = callPanel(session, { google: googleMeetSourceOf(google), meetWaitOver });
  const waitingForMeet = panel.link?.kind === 'meetPending';

  useEffect(() => {
    if (!waitingForMeet) return;
    const poll = window.setInterval(() => {
      void queryClient.invalidateQueries({ queryKey: [...BOOKING_ROOT, 'schedule'] });
    }, MEET_POLL_MS);
    const giveUp = window.setTimeout(() => {
      setMeetWaitOver(true);
    }, MEET_WAIT_MS);
    return () => {
      window.clearInterval(poll);
      window.clearTimeout(giveUp);
    };
  }, [waitingForMeet, queryClient]);

  const put = usePutCalendarEventIdCall({
    mutation: {
      onSuccess: (answer: CalendarEventResponse, variables) => {
        // The answer is the session as it now stands: drawn at once, then everything is read again.
        queryClient.setQueriesData<CalendarEventResponse[]>(
          { queryKey: [...BOOKING_ROOT, 'schedule'] },
          (old) => old?.map((event) => (event.id === answer.id ? answer : event)),
        );
        // The field or „Usuń link" is gone with this answer: focus must land somewhere that stays.
        if (field !== null || writeOf(variables.data) === 'removed') setReturnFocus(true);
        setField(null);
        setAskingOff(false);
        setRefusal(null);
        // A Meet newly asked for gets its own minute.
        if (variables.data?.isVideoCall === true && (!session.isVideoCall || variables.data.source === 'Meet')) {
          setMeetWaitOver(false);
        }
        announcer.announce(t(`${KEY}.done.${writeOf(variables.data)}`));
        void readBookingAgain(queryClient);
      },
      onError: (error) => {
        setAskingOff(false);
        const refused = writeRefusal(failureOf(error));
        setRefusal(`${t(`${KEY}.failed`)} ${t(refused.key)}`);
        if (refused.refetch) void readBookingAgain(queryClient);
      },
    },
  });

  const send = (data: PutCallRequest) => {
    if (put.isPending) return;
    setRefusal(null);
    put.mutate({ id: session.id, data });
  };

  const linkProblem = field !== null && field.tried && checkCallLink(field.value) === null ? t(`${KEY}.invalid`) : null;

  return {
    panel,
    field: field === null ? null : { value: field.value, problem: linkProblem },
    saving: put.isPending,
    returnFocus,
    focusReturned: () => {
      setReturnFocus(false);
    },
    refusal,
    prompt: askingOff
      ? {
          title: t(`${KEY}.offTitle`),
          description: t(`${KEY}.offBody`),
          cancelLabel: t('web:booking.actions.back'),
          confirmLabel: put.isPending ? t('web:booking.actions.saving') : t(`${KEY}.offConfirm`),
          destructiveConfirm: true,
          busy: put.isPending,
        }
      : null,
    announcement: announcer.message,
    setVideo: (on) => {
      if (put.isPending) return;
      if (!on && panel.toggle?.confirmOff === true) {
        setRefusal(null);
        setAskingOff(true);
        return;
      }
      send({ isVideoCall: on });
    },
    openField: () => {
      if (put.isPending) return;
      setRefusal(null);
      setField({ value: '', tried: false });
    },
    editField: (value) => {
      setField((current) => (current === null ? current : { ...current, value }));
    },
    closeField: () => {
      if (put.isPending) return;
      setField(null);
      setReturnFocus(true);
    },
    saveLink: () => {
      if (field === null) return;
      const link = checkCallLink(field.value);
      if (link === null) {
        setField({ ...field, tried: true });
        return;
      }
      send({ isVideoCall: true, link });
    },
    useMeet: () => {
      send({ isVideoCall: true, source: 'Meet' });
    },
    helperKey: callLinkHelperKey(isApplePlatform(globalThis.navigator)),
    removeLink: () => {
      send({ isVideoCall: true, link: null });
    },
    confirm: () => {
      send({ isVideoCall: false });
    },
    dismiss: () => {
      if (!put.isPending) setAskingOff(false);
    },
  };
}

function writeOf(data: PutCallRequest | undefined): CallWrite {
  if (data?.isVideoCall !== true) return 'off';
  if (data.source === 'Meet') return 'meet';
  if (data.link === undefined) return 'on';
  return data.link === null ? 'removed' : 'saved';
}
